import type { Participant, RoomCoreState, RoomState, StatePatchEvent } from '../types/room.types';
import type { ParticipantRole } from '../types/room.types';
import type { StudentExamSlot } from '../types/payloads/evaluacion.types';

export interface RoomClientState {
  connected: boolean;
  role: ParticipantRole | null;
  hostId: string | null;
  roomId: string | null;
  joinCode: string | null;
  participantId: string | null;
  sessionToken: string | null;
  state: RoomState | null;
  participants: Participant[];
  lastError: string | null;
  /** Bloque actual en evaluación autónoma (runtime del alumno) */
  examSlot: StudentExamSlot | null;
}

export type RoomClientAction =
  | { type: 'SET_CONNECTION'; payload: boolean }
  | { type: 'SET_ROLE'; payload: { role: ParticipantRole; hostId?: string } }
  | { type: 'SET_SESSION'; payload: { roomId: string; joinCode?: string; participantId?: string; sessionToken?: string } }
  | { type: 'STATE_SYNC'; payload: RoomState }
  | { type: 'STATE_PATCH'; payload: StatePatchEvent }
  | { type: 'SET_PARTICIPANTS'; payload: Participant[] }
  | { type: 'SET_EXAM_SLOT'; payload: StudentExamSlot | null }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'RESET' };

export const initialRoomClientState: RoomClientState = {
  connected: false,
  role: null,
  hostId: null,
  roomId: null,
  joinCode: null,
  participantId: null,
  sessionToken: null,
  state: null,
  participants: [],
  lastError: null,
  examSlot: null,
};
function mergeCore(base: RoomCoreState, patch?: Partial<RoomCoreState>): RoomCoreState {
  if (!patch) return base;
  return {
    ...base,
    ...patch,
    routing: patch.routing ? { ...base.routing, ...patch.routing } : base.routing,
    activity: patch.activity ? { ...base.activity, ...patch.activity } : base.activity,
    telemetry: patch.telemetry ? { ...base.telemetry, ...patch.telemetry } : base.telemetry,
  };
}

function applyPatchToState(current: RoomState | null, patchEvent: StatePatchEvent): RoomState | null {
  if (!current) return null;
  const patch = patchEvent.patch || {};

  return {
    core: mergeCore(current.core, patch.core),
    payload: patch.payload !== undefined ? patch.payload : current.payload,
    runtime: patch.runtime !== undefined ? patch.runtime : current.runtime,
    examSession: patch.examSession !== undefined ? patch.examSession : current.examSession,
    examDefinition: patch.examDefinition !== undefined ? patch.examDefinition : current.examDefinition,
  };
}

export function roomReducer(state: RoomClientState, action: RoomClientAction): RoomClientState {
  switch (action.type) {
    case 'SET_CONNECTION':
      return { ...state, connected: action.payload };

    case 'SET_ROLE':
      return {
        ...state,
        role: action.payload.role,
        hostId: action.payload.hostId ?? state.hostId,
      };

    case 'SET_SESSION':
      return {
        ...state,
        roomId: action.payload.roomId,
        joinCode: action.payload.joinCode ?? state.joinCode,
        participantId: action.payload.participantId ?? state.participantId,
        sessionToken: action.payload.sessionToken ?? state.sessionToken,
      };

    case 'STATE_SYNC':
      return {
        ...state,
        state: action.payload,
        joinCode: action.payload.core.joinCode,
        roomId: action.payload.core.roomId,
        lastError: null,
      };

    case 'STATE_PATCH': {
      if (!state.state) return state;
      const incomingVersion = action.payload.stateVersion ?? 0;
      const currentVersion = state.state.core.stateVersion ?? 0;
      if (incomingVersion > 0 && incomingVersion < currentVersion) {
        return state;
      }
      const nextState = applyPatchToState(state.state, action.payload);
      if (!nextState) return state;
      if (incomingVersion) {
        nextState.core.stateVersion = incomingVersion;
      }
      return { ...state, state: nextState, lastError: null };
    }

    case 'SET_PARTICIPANTS':
      return { ...state, participants: action.payload };

    case 'SET_EXAM_SLOT':
      return { ...state, examSlot: action.payload };

    case 'SET_ERROR':
      return { ...state, lastError: action.payload };

    case 'RESET':
      return { ...initialRoomClientState };

    default:
      return state;
  }
}
